import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useCases, useMails } from '@/shared/api/queries';
import { useVocabulary } from '@/shared/domain/useVocabulary';
import { DASH } from '@/shared/i18n';

import styles from './Omnibox.module.css';

interface Hit {
  readonly id: string;
  readonly title: string;
  readonly meta: string;
  readonly path: string;
}

const MAX_HITS = 8;

function matches(haystack: readonly string[], needle: string): boolean {
  return haystack.some((value) => value.toLowerCase().includes(needle));
}

export function Omnibox(): ReactElement {
  const navigate = useNavigate();
  const cases = useCases();
  const mails = useMails();
  const { t } = useTranslation('app');
  const vocabulary = useVocabulary();

  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const listId = useId();

  const hits = useMemo<readonly Hit[]>(() => {
    const needle = term.trim().toLowerCase();
    if (needle.length < 2) return [];

    const caseHits: Hit[] = (cases.data ?? [])
      .filter((record) =>
        matches(
          [record.id, vocabulary.caseCustomer(record), record.offering, record.order ?? ''],
          needle,
        ),
      )
      .map((record) => ({
        id: record.id,
        title: record.id,
        meta: `${vocabulary.caseCustomer(record)} · ${vocabulary.orderType(record.type)}`,
        path: `/case/${record.id}`,
      }));

    // A message is only useful as a route to the case it produced; searching for a
    // subject line and landing on a dead end would be worse than not matching.
    // `case` is a *reference*, and the mapper writes a dash rather than an empty
    // string when there is none — so the emptiness has to be asked about by name.
    // Read as a plain truthiness check it is never false, and every unbound
    // message routes to `/case/—`, which is the dead end this is here to avoid.
    const mailHits: Hit[] = (mails.data ?? [])
      .filter((mail) =>
        matches(
          [mail.id, vocabulary.mailSubject(mail.subject), vocabulary.mailCustomer(mail), mail.from],
          needle,
        ),
      )
      .map((mail) => ({
        id: mail.id,
        title: vocabulary.mailSubject(mail.subject),
        meta: `${vocabulary.mailCustomer(mail)} · ${mail.id}`,
        path: mail.case && mail.case !== DASH ? `/case/${mail.case}` : '/intake',
      }));

    return [...caseHits, ...mailHits].slice(0, MAX_HITS);
  }, [term, cases.data, mails.data, vocabulary]);

  useEffect(() => {
    setSelected(0);
  }, [term]);

  // Ctrl+K from anywhere: the operator's hands are already on the keyboard.
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        field.current?.focus();
        field.current?.select();
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    function onPointerDown(event: MouseEvent): void {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const expanded = open && term.trim().length >= 2;

  function go(hit: Hit): void {
    setOpen(false);
    setTerm('');
    field.current?.blur();
    void navigate(hit.path);
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!expanded || hits.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelected((index) => (index + 1) % hits.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelected((index) => (index - 1 + hits.length) % hits.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const hit = hits[selected];
      if (hit) go(hit);
    }
  }

  return (
    <div className={styles.omnibox} role="search" ref={wrapper}>
      <label className="co-sr-only" htmlFor="omni">
        {t('search.label')}
      </label>
      <input
        id="omni"
        ref={field}
        className={styles.input}
        type="text"
        placeholder={t('search.placeholder')}
        autoComplete="off"
        role="combobox"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-autocomplete="list"
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {expanded ? (
        <ul className={styles.results} id={listId} role="listbox" aria-label={t('search.results')}>
          {hits.length === 0 ? (
            <li className={styles.empty}>{t('search.empty', { term: term.trim() })}</li>
          ) : (
            hits.map((hit, index) => (
              <li
                key={`${hit.id}-${hit.path}`}
                className={
                  index === selected ? `${styles.result} ${styles.selected}` : styles.result
                }
                role="option"
                aria-selected={index === selected}
                onMouseEnter={() => setSelected(index)}
                onClick={() => go(hit)}
              >
                <b>{hit.title}</b>
                <span className={styles.meta}>{hit.meta}</span>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
